import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  createRuntimeDirective,
  createRuntimeUserUpdate,
  decideRuntimeApproval,
  decideRuntimeBlocker,
  flushStaleRuntimeTasks,
  hardResetRuntimeProject,
  formatApiError,
  getRuntimeProjects,
  getRuntimeTaskDetail,
  getRuntimeTasks,
  getRuntimeUserUpdates,
  uploadRuntimeAttachment,
} from '../lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  Briefcase,
  Clock3,
  Loader2,
  MessageSquare,
  Paperclip,
  PencilLine,
  RefreshCw,
  RotateCcw,
  Send,
  ShieldAlert,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import BuildBadge from '../components/BuildBadge';
import { Input } from '../components/ui/input';

const getExecutionAgentMetadata = (project) => {
  const metadata = project?.metadata || {};
  if (metadata?.agent_kind === 'agent_employee') return metadata;
  if (metadata?.product_type === 'execution_agent') return metadata;
  return null;
};

const statusBadgeClass = (status) => {
  switch (status) {
    case 'running':
    case 'active':
    case 'in_progress':
    case 'busy':
      return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25';
    case 'blocked':
    case 'failed':
    case 'rejected':
      return 'bg-amber-500/15 text-amber-300 border-amber-500/25';
    case 'planned':
    case 'queued':
    case 'ready':
      return 'bg-violet-500/15 text-violet-300 border-violet-500/25';
    case 'waiting_for_approval':
    case 'awaiting_approval':
    case 'pending':
      return 'bg-pink-500/15 text-pink-300 border-pink-500/25';
    case 'approved':
    case 'completed':
    case 'resolved':
      return 'bg-cyan-500/15 text-cyan-300 border-cyan-500/25';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const prettifyEventType = (value) => String(value || 'activity').replaceAll('.', ' ');

const parseObjectLike = (value) => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const summarizeTaskActivity = (detail) => {
  const latestEvent = detail?.events?.[0];
  if (latestEvent) {
    const payload = latestEvent.event_payload || {};
    return {
      title: prettifyEventType(latestEvent.event_type),
      body: payload.summary || payload.message || payload.description || payload.title || 'Recent activity recorded.',
      at: latestEvent.created_at,
    };
  }

  const latestDirective = detail?.directives?.[0];
  if (latestDirective) {
    return {
      title: `Directive: ${latestDirective.directive_type}`,
      body: latestDirective.reason || 'Operator directive issued.',
      at: latestDirective.created_at,
    };
  }

  const latestTool = detail?.tool_executions?.[0];
  if (latestTool) {
    return {
      title: `${latestTool.tool_name || 'Tool'} · ${latestTool.action_name || 'action'}`,
      body: latestTool.attempt_status || 'Tool activity recorded.',
      at: latestTool.started_at || latestTool.created_at,
    };
  }

  return {
    title: 'No recent activity',
    body: 'This task has not recorded any recent actions yet.',
    at: detail?.task?.updated_at || detail?.task?.created_at,
  };
};

const isBootstrapTask = (detail) => {
  const task = detail?.task || {};
  const metadata = task?.metadata || {};
  const combined = [
    task?.title,
    task?.objective,
    metadata?.source,
    metadata?.task_kind,
    metadata?.kind,
    metadata?.category,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return combined.includes('bootstrap') || combined.includes('initial setup') || combined.includes('initialization');
};

const buildPersistedChatMessages = (updates = []) => {
  return updates.flatMap((update) => {
    const parsedPayload = parseObjectLike(update?.parsed_payload);
    const assistant = parseObjectLike(parsedPayload?.assistant_message);
    const items = [];
    if (update?.raw_text) {
      items.push({
        id: `${update.id}-user`,
        role: 'operator',
        content: update.raw_text,
        created_at: update.created_at,
        pending: false,
        source: 'persisted',
      });
    }
    if (assistant?.content) {
      items.push({
        id: `${update.id}-assistant`,
        role: 'assistant',
        content: assistant.content,
        created_at: update.updated_at || update.created_at,
        pending: false,
        source: 'persisted',
      });
    }
    return items;
  }).slice(-32);
};

const mergeChatMessages = (current = [], persisted = []) => {
  const persistedKeys = new Set(
    persisted.map((message) => `${message.role}:${String(message.content || '').trim()}`)
  );
  const localToKeep = current.filter((message) => {
    const key = `${message.role}:${String(message.content || '').trim()}`;
    if (persistedKeys.has(key)) return false;
    return message?.pending || message?.source === 'response' || message?.source === 'failed';
  });
  return [...persisted, ...localToKeep]
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
    .slice(-32);
};

const ExecutionAgentDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskDetails, setTaskDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionBusy, setActionBusy] = useState('');
  const [operatorMessage, setOperatorMessage] = useState('');
  const [localMessages, setLocalMessages] = useState([]);
  const [showHardResetDialog, setShowHardResetDialog] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const operatorInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatScrollRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const lastAssistantCountRef = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, tasksRes, updatesRes] = await Promise.all([
        getRuntimeProjects(),
        getRuntimeTasks({ project_id: projectId }),
        getRuntimeUserUpdates({ project_id: projectId, limit: 50 }),
      ]);
      const selectedProject = (projectsRes.data || []).find((item) => item.id === projectId);
      if (!selectedProject) throw new Error('Execution agent not found');
      const taskRows = tasksRes.data || [];
      const persistedMessages = buildPersistedChatMessages(updatesRes.data || []);
      setProject(selectedProject);
      setTasks(taskRows);
      setLocalMessages((current) => mergeChatMessages(current, persistedMessages));

      const details = await Promise.all(
        taskRows.map(async (task) => {
          try {
            const detailRes = await getRuntimeTaskDetail(task.id);
            return detailRes.data;
          } catch (detailErr) {
            console.error(detailErr);
            return { task, events: [], interventions: { pending_approvals: [], open_blockers: [], counts: {} } };
          }
        })
      );
      setTaskDetails(details);
      setError('');
    } catch (err) {
      console.error(err);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Live refresh so the Work in Progress panel reflects backend changes
  // without requiring a manual page reload. Tasks that close, complete,
  // get cancelled, or gain new events will surface within ~8s. We pause
  // when the tab is hidden to avoid burning API calls in background.
  useEffect(() => {
    const REFRESH_INTERVAL_MS = 8000;
    let timerId = null;

    const start = () => {
      if (timerId) return;
      timerId = window.setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        fetchData();
      }, REFRESH_INTERVAL_MS);
    };

    const stop = () => {
      if (timerId) {
        window.clearInterval(timerId);
        timerId = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Catch up immediately when the tab comes back into focus, then
        // resume the polling cadence.
        fetchData();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stop();
    };
  }, [fetchData]);

  // Auto-scroll: only when a NEW message arrives AND the user is near the bottom.
  // Never scroll when messages are merely updated (pending→delivered) or when
  // taskDetails refreshes — that was snapping the user back mid-read.
  useEffect(() => {
    const count = localMessages.length;
    if (count > prevMessageCountRef.current && isNearBottomRef.current) {
      const node = chatScrollRef.current;
      if (node) window.requestAnimationFrame(() => { node.scrollTop = node.scrollHeight; });
    }
    prevMessageCountRef.current = count;
  }, [localMessages]);

  // Clear the typing indicator whenever a new assistant message arrives
  // (could come from the sync API response or via the polling refresh).
  useEffect(() => {
    const assistantCount = localMessages.filter((m) => m.role === 'assistant').length;
    if (assistantCount > lastAssistantCountRef.current) {
      lastAssistantCountRef.current = assistantCount;
      setIsAgentTyping(false);
    }
  }, [localMessages]);

  const executionMeta = useMemo(() => getExecutionAgentMetadata(project) || {}, [project]);
  const runtimeProfile = executionMeta.runtime_profile || {};
  const openTaskDetails = useMemo(
    () => taskDetails.filter((detail) => !['completed', 'cancelled', 'resolved'].includes(detail?.task?.status) && !isBootstrapTask(detail)),
    [taskDetails]
  );
  // Critical: never fall back to a cancelled/historical task when choosing
  // the "current" task for operator messages. After a reset, taskDetails[0]
  // is often the most recently cancelled task; using that as
  // suggested_task_id makes the backend behave as if old work still exists.
  const primaryTaskDetail = openTaskDetails[0] || null;
  const pendingApprovals = useMemo(
    () => taskDetails.flatMap((detail) => (detail?.interventions?.pending_approvals || []).map((item) => ({ ...item, taskTitle: detail?.task?.title, taskId: detail?.task?.id }))),
    [taskDetails]
  );
  const openBlockers = useMemo(
    () => taskDetails.flatMap((detail) => (detail?.interventions?.open_blockers || []).map((item) => ({ ...item, taskTitle: detail?.task?.title, taskId: detail?.task?.id }))),
    [taskDetails]
  );
  // Activity feed combines two sources of runtime truth:
  //   1. runtime_event_log (events) — high-level lifecycle signals
  //      (task created, step progressed, checkpoint written, etc.).
  //   2. runtime_tool_executions — every call the agent made to a real
  //      tool. This is where tenant DB tool calls (db.execute_ddl etc.)
  //      become visible to the operator. Without this merge, an
  //      operator watching the activity pane would never see that
  //      Cassie ran `CREATE TABLE clients ...` — they'd only see a
  //      generic 'task activity recorded' event.
  const activityFeed = useMemo(() => {
    const eventItems = taskDetails.flatMap((detail) =>
      (detail?.events || []).slice(0, 6).map((event) => ({
        id: `event-${event.id}`,
        kind: 'event',
        taskTitle: detail?.task?.title,
        type: event.event_type,
        body:
          event.event_payload?.summary ||
          event.event_payload?.message ||
          event.event_payload?.description ||
          event.event_payload?.title ||
          'Task activity recorded.',
        created_at: event.created_at,
      }))
    );

    const toolItems = taskDetails.flatMap((detail) =>
      (detail?.tool_executions || []).slice(0, 6).map((tool) => {
        const toolName = tool.tool_name || 'tool';
        const actionName = tool.action_name || '';
        const status = tool.attempt_status || 'unknown';
        // DB tools get a friendlier label + SQL preview so the
        // operator can see at a glance what Cassie did.
        const isDbTool = typeof toolName === 'string' && toolName.startsWith('db.');
        const input = tool.input_payload || {};
        const output = tool.output_payload || {};
        const error = tool.error_payload || {};
        let title = `${toolName} · ${actionName || 'action'}`;
        let body = status;
        if (isDbTool) {
          const sql = typeof input.sql === 'string' ? input.sql.trim() : '';
          const rowCount =
            typeof output.row_count === 'number' ? output.row_count : null;
          title = `${toolName} · ${actionName || 'sql'}`;
          if (status === 'succeeded') {
            const sqlPreview = sql
              ? sql.length > 140
                ? `${sql.slice(0, 140)}…`
                : sql
              : '(metadata read)';
            body =
              rowCount !== null
                ? `✅ ${sqlPreview} — ${rowCount} row(s)`
                : `✅ ${sqlPreview}`;
          } else if (status === 'failed') {
            const errMsg =
              error?.error || error?.message || 'execution failed';
            const sqlPreview = sql
              ? sql.length > 100
                ? `${sql.slice(0, 100)}…`
                : sql
              : '';
            body = sqlPreview
              ? `❌ ${sqlPreview} — ${errMsg}`
              : `❌ ${errMsg}`;
          }
        }
        return {
          id: `tool-${tool.id}`,
          kind: isDbTool ? 'db_tool' : 'tool',
          taskTitle: detail?.task?.title,
          type: toolName,
          title,
          body,
          created_at: tool.started_at || tool.created_at,
        };
      })
    );

    return [...eventItems, ...toolItems]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 18);
  }, [taskDetails]);

  const runAction = async (label, fn) => {
    setActionBusy(label);
    try {
      await fn();
      await fetchData();
    } catch (err) {
      console.error(err);
      setError(formatApiError(err));
    } finally {
      setActionBusy('');
    }
  };

  const handleStartNewSession = () => {
    setLocalMessages([]);
    setOperatorMessage('');
    setError('');
  };

  const handleRestartAgentView = async () => {
    if (actionBusy) return;
    setError('');
    setLoading(true);
    try {
      await flushStaleRuntimeTasks(projectId);
      await fetchData();
      setLocalMessages([]);
      setOperatorMessage('');
      window.location.reload();
    } catch (err) {
      console.error(err);
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  const handleHardReset = async () => {
    setShowHardResetDialog(false);
    setActionBusy('hard-reset');
    setError('');
    try {
      await hardResetRuntimeProject(projectId);
      setLocalMessages([]);
      setOperatorMessage('');
      window.location.reload();
    } catch (err) {
      console.error(err);
      setError(formatApiError(err));
    } finally {
      setActionBusy('');
    }
  };

  const handleStopTask = async (taskId) => {
    await runAction(`stop-task-${taskId}`, () =>
      createRuntimeDirective(taskId, { directive_type: 'cancel', reason: 'Stopped from agent console' })
    );
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsUploading(true);
    for (const file of files) {
      try {
        const res = await uploadRuntimeAttachment(file, projectId);
        const data = res?.data;
        if (data?.artifact_id) {
          setPendingAttachments((prev) => [...prev, {
            artifact_id: data.artifact_id,
            filename: data.filename,
            content_type: data.content_type,
            byte_size: data.byte_size,
            download_url: data.download_url,
          }]);
        }
      } catch (err) {
        console.error('Attachment upload failed:', err);
        setError(formatApiError(err));
      }
    }
    setIsUploading(false);
  };

  const handleRemoveAttachment = (artifactId) => {
    setPendingAttachments((prev) => prev.filter((a) => a.artifact_id !== artifactId));
  };

  const handleSendOperatorMessage = async () => {
    if ((!operatorMessage.trim() && pendingAttachments.length === 0) || isSending) return;
    const rawText = operatorMessage.trim();
    const attachmentsSnapshot = [...pendingAttachments];

    const optimisticUserId = `${Date.now()}-user`;
    const optimisticContent = rawText || (attachmentsSnapshot.length > 0 ? `[${attachmentsSnapshot.map((a) => a.filename).join(', ')}]` : '');
    setLocalMessages((current) => [...current, {
      id: optimisticUserId,
      role: 'operator',
      content: optimisticContent,
      created_at: new Date().toISOString(),
      pending: false,
      source: 'optimistic',
      attachments: attachmentsSnapshot,
    }]);
    setOperatorMessage('');
    setPendingAttachments([]);
    setIsAgentTyping(true);
    setIsSending(true);
    window.requestAnimationFrame(() => operatorInputRef.current?.focus());

    try {
      const response = await createRuntimeUserUpdate({
        project_id: projectId,
        task_id: null,
        raw_text: rawText || `[Attached: ${attachmentsSnapshot.map((a) => a.filename).join(', ')}]`,
        parsed_payload: {
          source: 'agent_employee_console',
          open_task_ids: openTaskDetails.map((detail) => detail?.task?.id).filter(Boolean),
          suggested_task_id: primaryTaskDetail?.task?.id || null,
          attachment_ids: attachmentsSnapshot.map((a) => a.artifact_id),
          attachments: attachmentsSnapshot,
        },
        status: 'received',
      });
      const updateId = response?.data?.id;
      // Swap in the real persisted id
      if (updateId) {
        setLocalMessages((current) => current.map((message) => (
          message.id === optimisticUserId ? { ...message, id: `${updateId}-user` } : message
        )));
      }
      // If the API returned a sync reply (e.g. a note/clarification), show it immediately.
      const assistantMessage = response?.data?.assistant_message;
      if (assistantMessage?.content) {
        const assistantEntry = {
          id: updateId ? `${updateId}-assistant` : `${Date.now()}-assistant`,
          role: 'assistant',
          content: assistantMessage.content,
          created_at: new Date().toISOString(),
          pending: false,
          source: 'response',
        };
        setLocalMessages((current) => {
          const alreadyPresent = current.some((m) => m.id === assistantEntry.id || (m.role === 'assistant' && m.content === assistantEntry.content));
          return alreadyPresent ? current : [...current, assistantEntry];
        });
        // isAgentTyping will clear via the assistant count effect above
      }
      await fetchData();
    } catch (err) {
      console.error(err);
      setIsAgentTyping(false);
      setLocalMessages((current) => current.map((message) => (
        message.id === optimisticUserId ? { ...message, source: 'failed' } : message
      )));
      setError(formatApiError(err));
    } finally {
      setIsSending(false);
    }
  };

  if (loading) {
    return <Layout><div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-8 space-y-6" data-testid="execution-agent-detail-page">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Link to="/agents" className="inline-flex items-center gap-2 hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to Agent Dashboard
          </Link>
        </div>

        {error ? <div className="apollo-card p-4 text-sm text-red-400">{error}</div> : null}

        {project ? (
          <>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap min-w-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline">Agent employee</Badge>
                    <Badge className={statusBadgeClass(project.status || 'active')}>{project.status || 'active'}</Badge>
                  </div>
                  <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">{runtimeProfile.name || project.title}</h1>
                  <p className="text-muted-foreground text-sm mt-0.5">Agent Console · {runtimeProfile.role || 'Operational agent employee'}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 rounded-lg border border-border/70 bg-card px-3 py-1.5 text-sm">
                    <span className="text-muted-foreground">Tasks</span>
                    <span className="font-semibold">{openTaskDetails.length}</span>
                  </div>
                  {pendingApprovals.length > 0 && (
                    <div className="flex items-center gap-1.5 rounded-lg border border-pink-500/40 bg-pink-500/10 px-3 py-1.5 text-sm text-pink-300">
                      <span>Approvals</span>
                      <span className="font-semibold">{pendingApprovals.length}</span>
                    </div>
                  )}
                  {openBlockers.length > 0 && (
                    <div className="flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-300">
                      <span>Blockers</span>
                      <span className="font-semibold">{openBlockers.length}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <BuildBadge />
                <Button variant="outline" size="sm" onClick={() => navigate(`/agent-employees/${project.id}/edit`)}>
                  <PencilLine className="w-4 h-4 mr-2" /> Configure
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_0.85fr] gap-6">
              <div className="space-y-6">
                <Card className="apollo-card overflow-hidden border-primary/20 shadow-sm shadow-primary/5">
                  <CardHeader className="border-b border-border">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <CardTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" /> Chat with this agent</CardTitle>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handleStartNewSession} disabled={!!actionBusy}>
                          <RotateCcw className="w-4 h-4 mr-2" /> New session
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleRestartAgentView} disabled={!!actionBusy || loading}>
                          <RefreshCw className="w-4 h-4 mr-2" /> Restart agent
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {/*
                      Chat area — ONLY real chat turns live here.
                      Task lifecycle events (task.created, plan.*, etc.) are
                      rendered below in the Work-in-Progress card and in the
                      Activity section; injecting them inline here was making
                      each user message appear 3-4 times on screen.
                    */}
                    <div
                      ref={chatScrollRef}
                      className="p-4 space-y-3 overflow-y-auto bg-secondary/10"
                      style={{ minHeight: '60vh', maxHeight: '70vh' }}
                      onScroll={() => {
                        const node = chatScrollRef.current;
                        if (!node) return;
                        isNearBottomRef.current = node.scrollTop + node.clientHeight >= node.scrollHeight - 120;
                      }}
                    >
                      {localMessages.map((message) => (
                        <div key={message.id} className={`chat-message ${message.role === 'assistant' ? 'assistant' : 'user'}`}>
                          <div>{message.content}</div>
                          {(message.attachments || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {message.attachments.map((a) => (
                                <a
                                  key={a.artifact_id}
                                  href={`${process.env.REACT_APP_BACKEND_URL}/api${a.download_url}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1.5 rounded border border-border/60 bg-secondary/40 px-2 py-1 text-xs hover:bg-secondary/70 transition-colors"
                                >
                                  <Paperclip className="w-3 h-3 opacity-60" />
                                  {a.filename}
                                </a>
                              ))}
                            </div>
                          )}
                          {message.source === 'failed' ? <div className="mt-1 text-[11px] text-amber-300">Send failed — backend did not fully apply this update.</div> : null}
                        </div>
                      ))}
                      {isAgentTyping && (
                        <div className="chat-message assistant">
                          <div className="flex items-center gap-1 py-0.5">
                            <span className="w-2 h-2 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-4 border-t border-border bg-card space-y-2">
                      {pendingAttachments.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {pendingAttachments.map((a) => (
                            <div key={a.artifact_id} className="inline-flex items-center gap-1.5 rounded border border-border/60 bg-secondary/40 px-2 py-1 text-xs">
                              <Paperclip className="w-3 h-3 opacity-60" />
                              <span className="max-w-[140px] truncate">{a.filename}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveAttachment(a.artifact_id)}
                                className="opacity-50 hover:opacity-100 ml-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleFileSelect}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          title="Attach file"
                        >
                          {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                        </Button>
                        <Input
                          ref={operatorInputRef}
                          value={operatorMessage}
                          onChange={(e) => setOperatorMessage(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendOperatorMessage()}
                          placeholder={'Send an instruction, clarification, or question…'}
                        />
                        <Button
                          onClick={handleSendOperatorMessage}
                          disabled={(!operatorMessage.trim() && pendingAttachments.length === 0) || isSending}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="apollo-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-primary" /> Work in progress</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {openTaskDetails.length ? openTaskDetails.map((detail) => {
                      const activity = summarizeTaskActivity(detail);
                      return (
                        <div key={detail.task.id} className="rounded-xl border border-border/70 p-4 space-y-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="font-semibold">{detail.task.title}</div>
                              <div className="text-sm text-muted-foreground mt-1">{detail.task.objective || 'No objective recorded.'}</div>
                            </div>
                            <Badge className={statusBadgeClass(detail.task.status)}>{detail.task.status || 'active'}</Badge>
                          </div>

                          <div className="grid gap-3 md:grid-cols-3 text-sm">
                            <div className="rounded-xl border border-border/70 p-3">
                              <div className="text-xs uppercase tracking-wide text-muted-foreground">Latest action</div>
                              <div className="font-medium mt-2">{activity.title}</div>
                              <div className="text-muted-foreground mt-1 line-clamp-2">{activity.body}</div>
                            </div>
                            <div className="rounded-xl border border-border/70 p-3">
                              <div className="text-xs uppercase tracking-wide text-muted-foreground">Pending approvals</div>
                              <div className="text-2xl font-semibold mt-2">{detail.interventions?.counts?.pending_approvals || 0}</div>
                            </div>
                            <div className="rounded-xl border border-border/70 p-3">
                              <div className="text-xs uppercase tracking-wide text-muted-foreground">Open blockers</div>
                              <div className="text-2xl font-semibold mt-2">{detail.interventions?.counts?.open_blockers || 0}</div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                            <div className="inline-flex items-center gap-2"><Clock3 className="w-4 h-4" /> {formatDateTime(activity.at)}</div>
                            <Button size="sm" variant="destructive" disabled={!!actionBusy} onClick={() => handleStopTask(detail.task.id)}>
                              {actionBusy === `stop-task-${detail.task.id}` ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                              Stop task
                            </Button>
                          </div>
                        </div>
                      );
                    }) : <div className="text-sm text-muted-foreground">No open tasks yet.</div>}
                  </CardContent>
                </Card>

                <Card className="apollo-card">
                  <CardHeader>
                    <CardTitle>Recent activity</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {activityFeed.length ? activityFeed.map((item) => (
                      <div
                        key={item.id}
                        className={`rounded-xl border p-4 ${
                          item.kind === 'db_tool'
                            ? 'border-primary/40 bg-primary/5'
                            : 'border-border/70'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium">
                              {item.title || prettifyEventType(item.type)}
                            </div>
                            <div className="text-muted-foreground mt-1 whitespace-pre-wrap break-words font-mono text-xs">
                              {item.body}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(item.created_at)}</div>
                        </div>
                        <div className="text-xs text-muted-foreground mt-2">{item.taskTitle}</div>
                      </div>
                    )) : <div className="text-muted-foreground">No recent activity recorded yet.</div>}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="apollo-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-primary" /> Pending approvals</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {pendingApprovals.length ? pendingApprovals.map((approval) => (
                      <div key={approval.id} className="rounded-xl border border-border/70 p-4 space-y-3">
                        <div>
                          <div className="font-medium">{approval.title || 'Approval needed'}</div>
                          <div className="text-muted-foreground mt-1">{approval.description || 'No description recorded.'}</div>
                          <div className="text-xs text-muted-foreground mt-2">{approval.taskTitle}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" disabled={!!actionBusy} onClick={() => runAction(`approve-${approval.id}`, () => decideRuntimeApproval(approval.id, { status: 'approved', decision_summary: 'Approved from agent employee console' }))}>Approve</Button>
                          <Button size="sm" variant="destructive" disabled={!!actionBusy} onClick={() => runAction(`reject-${approval.id}`, () => decideRuntimeApproval(approval.id, { status: 'rejected', decision_summary: 'Rejected from agent employee console' }))}>Reject</Button>
                        </div>
                      </div>
                    )) : <div className="rounded-xl border border-border/70 p-4 text-muted-foreground">Nothing waiting for approval.</div>}
                  </CardContent>
                </Card>

                <Card className="apollo-card">
                  <CardHeader>
                    <CardTitle>Open blockers</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {openBlockers.length ? openBlockers.map((blocker) => (
                      <div key={blocker.id} className="rounded-xl border border-border/70 p-4 space-y-3">
                        <div>
                          <div className="font-medium">{blocker.title || 'Blocker'}</div>
                          <div className="text-muted-foreground mt-1">{blocker.description || blocker.resolution_summary || 'No details recorded.'}</div>
                          <div className="text-xs text-muted-foreground mt-2">{blocker.taskTitle}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" disabled={!!actionBusy} onClick={() => runAction(`resolve-${blocker.id}`, () => decideRuntimeBlocker(blocker.id, { status: 'resolved', resolution_summary: 'Resolved from agent employee console' }))}>Resolve</Button>
                          <Button size="sm" variant="outline" disabled={!!actionBusy} onClick={() => runAction(`dismiss-${blocker.id}`, () => decideRuntimeBlocker(blocker.id, { status: 'dismissed', resolution_summary: 'Dismissed from agent employee console' }))}>Dismiss</Button>
                        </div>
                      </div>
                    )) : <div className="rounded-xl border border-border/70 p-4 text-muted-foreground">No open blockers.</div>}
                  </CardContent>
                </Card>

                <Card className="apollo-card border-red-500/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-400">
                      <ShieldAlert className="w-5 h-5" /> Danger zone
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="rounded-xl border border-red-500/20 p-4 space-y-3">
                      <div>
                        <div className="font-medium">Reset all work</div>
                        <div className="text-muted-foreground mt-1">Cancels every active run and all open tasks. Use this to fully clear the agent's work queue and start fresh.</div>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={!!actionBusy}
                        onClick={() => setShowHardResetDialog(true)}
                      >
                        {actionBusy === 'hard-reset' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Reset all work
                      </Button>
                    </div>
                  </CardContent>
                </Card>

              </div>
            </div>

            <AlertDialog open={showHardResetDialog} onOpenChange={setShowHardResetDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset all work?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will cancel every active run and all open tasks for this agent. This cannot be undone — the agent will need new instructions to resume work.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleHardReset} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Yes, reset all work
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : null}
      </div>
    </Layout>
  );
};

export default ExecutionAgentDetailPage;
